// src/components/WeightSelect.tsx
//
// Weight-class picker shared by the coach Register page, the admin Manual
// Registration modal and the Edit Registration modal.
//
// Behaviour (per federation request): NON-restrictive. Every competition weight
// stays selectable. The weights that officially correspond to the athlete's
// category + division are simply pulled to the top of the <select> (an "★
// Recommended" optgroup) AND shown as red, tappable chips right below — so a
// coach notices the right ones instantly but can still choose anything.

import React from "react";
import { C, FONT } from "../theme";
import { splitWeights } from "../utils/weightSuggestions";
import { categoryDisplayLabel } from "../utils/rankingCalculator";

interface Props {
  value: string;
  onChange: (w: string) => void;
  /** All weights the competition offers for the chosen division. */
  weights: string[];
  /** Official weights for the chosen category+division (advisory). May be []. */
  recommended: string[];
  category?: string;
  division?: string;
  placeholder: string;
  selectStyle: React.CSSProperties;
  disabled?: boolean;
  /** Optional translator; falls back to built-in English when omitted. */
  t?: (k: string) => string;
}

const DEFAULTS: Record<string, string> = {
  "weightSelect.recommendedFor": "Recommended for {cat}",
  "weightSelect.recommended": "Recommended weights",
  "weightSelect.other": "Other weights",
  "weightSelect.note": "These match the official table — you can still pick any weight.",
  "publicRegs.divisionMale": "Male",
  "publicRegs.divisionFemale": "Female",
};

export default function WeightSelect({
  value,
  onChange,
  weights,
  recommended,
  category,
  division,
  placeholder,
  selectStyle,
  disabled,
  t,
}: Props) {
  const tr = (k: string) => {
    const v = t ? t(k) : undefined;
    return v && v !== k ? v : DEFAULTS[k] ?? k;
  };

  const { recommended: rec, other } = splitWeights(weights, recommended);
  const showGroups = rec.length > 0 && other.length > 0;

  const catLabel = category ? categoryDisplayLabel(category) : "";
  const divLabel = division
    ? String(division).toLowerCase().startsWith("f")
      ? tr("publicRegs.divisionFemale")
      : tr("publicRegs.divisionMale")
    : "";
  const context = [catLabel, divLabel].filter(Boolean).join(" · ");
  const recHeading = context
    ? tr("weightSelect.recommendedFor").replace("{cat}", context)
    : tr("weightSelect.recommended");

  return (
    <div>
      <select
        style={selectStyle}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {showGroups ? (
          <>
            <optgroup label={`★ ${recHeading}`}>
              {rec.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </optgroup>
            <optgroup label={tr("weightSelect.other")}>
              {other.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </optgroup>
          </>
        ) : (
          weights.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))
        )}
      </select>

      {rec.length > 0 && !disabled && (
        <div style={cs.wrap}>
          <span style={cs.heading}>★ {recHeading}</span>
          <div style={cs.row}>
            {rec.map((w) => {
              const active = value === w;
              return (
                <button
                  type="button"
                  key={w}
                  onClick={() => onChange(w)}
                  style={{ ...cs.chip, ...(active ? cs.chipActive : {}) }}
                >
                  {w}
                </button>
              );
            })}
          </div>
          <span style={cs.note}>{tr("weightSelect.note")}</span>
        </div>
      )}
    </div>
  );
}

const cs: Record<string, React.CSSProperties> = {
  wrap: { marginTop: 8, display: "flex", flexDirection: "column", gap: 5 },
  heading: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: C.red,
  },
  row: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    backgroundColor: C.redDim,
    color: C.red,
    border: `1px solid ${C.red}`,
    borderRadius: 16,
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    minHeight: 32,
    lineHeight: 1.1,
  },
  chipActive: {
    backgroundColor: C.red,
    color: "#fff",
  },
  note: { fontSize: 10.5, color: C.text3, lineHeight: 1.4 },
};
